import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, throwError } from 'rxjs';
import { catchError, delay, map } from 'rxjs/operators';
import { TuwaiqUtil } from 'src/app/core/TuwaiqUtil';
import { ALERT_POPUP_STATUS } from 'src/app/core/constants/alert-popup-status.enum';
import { ApiConstants } from 'src/app/core/constants/api.constants';
import { EServicesConstants } from 'src/app/core/constants/user-constants/e-services.constants';
import { ticketingConstants } from 'src/app/core/constants/user-constants/ticketing.constants';
import { AttachmentFile } from 'src/app/core/models/attachment-file.model';
import { Attachment } from 'src/app/core/models/attachment.model';
import { FilterDetails } from 'src/app/core/models/filter-details.model';
import { RequestDetailsResponse } from 'src/app/core/models/request-details-response.modal';
import { RequestResponse } from 'src/app/core/models/request-response.model';
import { EServiceHistoryFeildsTableColumns, EServiceTableColumns } from 'src/app/core/models/table-columns/e-service-table-columns.model';
import { TicketingTableColumns } from 'src/app/core/models/table-columns/ticketing-table-columns.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { LANGUAGES, TranslateService } from 'src/app/core/translate/translate.service';
import { SessionExpiredComponent } from 'src/app/shared/components/sessionExpired/sessionExpired/session-expired.component';
import { TicketingRequestModal } from '../util/model/ticketing.model';
@Injectable({
    providedIn: 'root'
})
export class TicketingService {
    onOpenDialogClick = new Subject<string>();
    onReloadRequests = new Subject<boolean>();
    onCloseDialog = new Subject<boolean>();
    onRequestAssigned = new Subject<boolean>();
    showTableLoader = new Subject<boolean>();
    filterRequestList = new Subject<FilterDetails[]>();
    fetchNextPage = new Subject<number>();
    updateCount = new Subject<boolean>();
    passReturnedRequestDetails = new Subject<Comment>();
    showLoaderOverlay = new Subject<boolean>();
    scrollToTop = new Subject<boolean>();
    removeAttachment = new Subject<AttachmentFile | Attachment>();
    selectedRequests: number[] = [];
    requestDetails: RequestDetailsResponse;
    updateAlertPopupStatus = new Subject<{
        status: ALERT_POPUP_STATUS;
        message: string;
    }>();
    alertPopupConfirm = new Subject<boolean>();
    alertPopupReviwedConfirm = new Subject<boolean>();

    clearAttachments = new Subject<boolean>();
    dateRangeChanged = new Subject<boolean>();
    onDataEdit = new Subject<any>();
    onDataEditStatus = new Subject<any>();
    deleteSelectedUser = new Subject<any>();
    isExpiredSession: any;
    lang: any;

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        public dialog: MatDialog,
        private translateService: TranslateService
    ) {
        this.translateService.currentLang$
            .pipe(
                delay(100),
                map(lang => {
                    this.lang = lang;
                })
            )
            .subscribe();
    }

    getTicketingList(parameters?: HttpParams) {
        let api = ApiConstants.TICKETING_LIST;

        return this.http.get(api, { params: parameters, observe: 'response' }).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getTicketingDetails(ticketingId: number) {
        let api = ApiConstants.TICKETING_DETAILS.replace('{messageId}', ticketingId.toString());

        return this.http.get(api).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getUsersSender(parameters?: HttpParams) {
        let api = ApiConstants.USER_SENDER_LIST;

        return this.http.get(api, { params: parameters, observe: 'response' }).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    sendTicketing(ticketing: TicketingRequestModal) {
        const api = ApiConstants.SEND_TICKETING;
        return this.http.post(api, ticketing).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    retryTicketing(messageId: number, recipientIDs?: number[]) {
        let api = ApiConstants.RETRY_TICKETING.replace('{messageId}', messageId.toString());

        // If recipientIDs are provided, include them in payload, otherwise send empty object
        const payload = recipientIDs?.length ? { 'receiver-ids': recipientIDs } : {};

        return this.http.patch(api, payload).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    convertResponseToTableData(response: RequestResponse) {
        return new TicketingTableColumns(
            response.id,
            response.sender.name,
            response.createdAt,
            response.receiver?.name,
            response.receiverPhoneNumber,
            response.body
        );
    }
    convertEServicesResponseToTableData(response: any) {
        return new EServiceTableColumns(
            response?.number,
            response?.prefixCode,
            this.lang === LANGUAGES.AR ? response?.requester?.nameAr : response?.requester?.nameEn, //response?.owner?.nameEn ||
            response?.createdAt,
            this.lang === LANGUAGES.AR ? response?.mainTicketTypeDto?.nameAr : response?.mainTicketTypeDto?.nameEn,
            this.lang === LANGUAGES.AR ? response?.subTicketTypeDto?.nameAr : response?.subTicketTypeDto?.nameEn,
            // response?.status,
            response?.ticketStatusDto?.ticketStatusId || '',
            response?.subTicketTypeDto?.priority,
            response?.createdAt,
            response?.owner?.name || '',
            response?.mainTicketTypeDto?.departmentDto?.name || '',
        );
    }

    convertEServicesHistoryFieldsResponseToTableData(response: any) {
        return new EServiceHistoryFeildsTableColumns(
            response?.id,
            response?.fieldName,
            response?.oldValue,
            response?.newValue,
            response?.updatedBy?.image,
            response?.updatedBy?.name,
            response?.changedAt,
            response?.fieldType,
            response?.action
        );
    }
    getTableColumns(requestObjectName: string) {
        return ticketingConstants[requestObjectName].tableColumn;
    }

    getTableHeaders(requestObjectName: string) {
        const displayColumns = ticketingConstants[requestObjectName].tableColumn.map(c => c.columnDef);

        const tableWithSelection = ticketingConstants[requestObjectName].tableWithSelection;
        if (tableWithSelection) {
            displayColumns.unshift('select');
        }

        return displayColumns;
    }
    getEServicesTableColumns(requestObjectName: string) {
        return EServicesConstants[requestObjectName].tableColumn;
    }
    getEServicesTableHeaders(requestObjectName: string) {
        const displayColumns = EServicesConstants[requestObjectName].tableColumn.map(c => c.columnDef);

        const tableWithSelection = EServicesConstants[requestObjectName].tableWithSelection;
        if (tableWithSelection) {
            displayColumns.unshift('select');
        }

        return displayColumns;
    }
    isEServicesTableWithSelection(requestObjectName: string) {
        return EServicesConstants[requestObjectName].tableWithSelection;
    }
    isTableWithSelection(requestObjectName: string) {
        return ticketingConstants[requestObjectName].tableWithSelection;
    }

    /** Error Handling method */
    errorHandler(errorResponse: HttpErrorResponse) {
        if (errorResponse.status === 401) {
            // this.authService.logout()
            this.handleSessionExpired();
        } else if (errorResponse.error && errorResponse.error['errors']) {
            return throwError(() => errorResponse.error['errors'][0]);
        }
        return throwError(() => 'An error occurred; please try again later.');
    }

    //TEMP:
    errorListHandler(errorResponse: HttpErrorResponse) {
        if (errorResponse.status === 401) {
            // this.authService.logout()
            this.handleSessionExpired();
        } else if (errorResponse.error && errorResponse.error['errors']) {
            return throwError(() => errorResponse.error['errors']);
        }
        return throwError(() => 'An error occurred; please try again later.');
    }

    handleSessionExpired() {
        if (!this.isExpiredSession) {
            this.dialog.open(SessionExpiredComponent, {
                width: '412px',
                disableClose: true
            });
            this.isExpiredSession = true;
        }
    }
}
